import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, Link as LinkIcon, ShieldAlert, EyeOff, TrendingDown, ShieldCheck, Brain, Banknote, Users, Lock, Shield, Server, Scan, Sparkles, Target, Palette, TrendingUp, Rocket, DollarSign, BarChart3, Building2, Globe, Handshake, Cpu, Database, Layers, Award, LogOut, Download } from 'lucide-react';
export function PitchDeck() {
  const [activeSlide, setActiveSlide] = useState(0);
  const totalSlides = 20;
  const containerRef = useRef<HTMLDivElement>(null);
  // Handle Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't interfere if user is typing in an input (though there are none here)
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) return;
      const isNext = ['ArrowDown', 'ArrowRight', ' '].includes(e.key);
      const isPrev = ['ArrowUp', 'ArrowLeft'].includes(e.key);
      if (isNext || isPrev) {
        e.preventDefault();
        const slides = document.querySelectorAll('.slide');
        let targetIndex = activeSlide;
        if (isNext && activeSlide < totalSlides - 1) {
          targetIndex = activeSlide + 1;
        } else if (isPrev && activeSlide > 0) {
          targetIndex = activeSlide - 1;
        }
        if (targetIndex !== activeSlide && slides[targetIndex]) {
          slides[targetIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, {
      passive: false
    });
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeSlide, totalSlides]);
  // Handle Intersection Observer for active dot and animations
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = Number(entry.target.getAttribute('data-index'));
          setActiveSlide(index);
          entry.target.classList.add('is-visible');
        }
      });
    }, {
      threshold: 0.5
    });
    const slides = document.querySelectorAll('.slide');
    slides.forEach(slide => observer.observe(slide));
    return () => observer.disconnect();
  }, []);
  const scrollToSlide = (index: number) => {
    const slides = document.querySelectorAll('.slide');
    if (slides[index]) {
      slides[index].scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  return <div className="w-full h-full bg-[#060b18] text-[#f0f4ff] font-body" ref={containerRef} data-id="element-1" data-mp="1wqd6uc425ltm">
      {/* Download PDF Button */}
      <button onClick={() => window.print()} className="print-hide fixed top-5 left-5 z-50 flex items-center gap-2 bg-[#111d35] border border-[#00c8ff]/20 text-[#7a8ba8] hover:text-[#00c8ff] hover:border-[#00c8ff]/40 hover:shadow-[0_0_15px_rgba(0,200,255,0.15)] px-4 py-2 rounded-full text-sm font-body transition-all duration-300" aria-label="Download as PDF" data-id="element-2" data-mp="21hldj0e06t33">
        <Download className="w-4 h-4" data-id="element-3" data-mp="wj815f9w9x8b" />
        <span data-id="element-4" data-mp="wg3rw3hkobas">Download PDF</span>
      </button>

      {/* Navigation Dots */}
      <div className="nav-dots fixed right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-2" data-id="element-5" data-mp="rh0lofpuvz18">
        {Array.from({
        length: totalSlides
      }).map((_, i) => <button key={i} onClick={() => scrollToSlide(i)} className={`w-2 h-2 rounded-full transition-all duration-300 ${activeSlide === i ? 'bg-[#00c8ff] scale-125 shadow-[0_0_10px_rgba(0,200,255,0.8)]' : 'bg-[#7a8ba8]/40 hover:bg-[#7a8ba8]/80'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-6" data-mp="iix9ia0rgem3" data-mpi={i} />)}
      </div>

      {/* Slide 1: Title (data-index=0) */}
      <section className="slide" data-index="0" data-id="element-7" data-mp="3vwif7b4cqy3n">
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" data-id="element-8" data-mp="3qy74yza5alkw">
          <div className="absolute top-1/4 left-1/4 w-[40vw] h-[40vw] max-w-lg max-h-lg bg-[#00c8ff] rounded-full mix-blend-screen filter blur-[100px] opacity-20 animate-pulse-glow" data-id="element-9" data-mp="1uusp40hn267a"></div>
          <div className="absolute bottom-1/4 right-1/4 w-[40vw] h-[40vw] max-w-lg max-h-lg bg-[#6366f1] rounded-full mix-blend-screen filter blur-[100px] opacity-20 animate-pulse-glow" style={{
          animationDelay: '2s'
        }} data-id="element-10" data-mp="1yy8miz1j8al1"></div>
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_70%,transparent_100%)]" data-id="element-11" data-mp="1b8xt62mihigx"></div>
        </div>

        <div className="slide-content text-center" data-id="element-12" data-mp="177c83tztpxvn">
          <div className="mb-6 animate-on-view flex justify-center" data-id="element-13" data-mp="2wjwk3n9q4yy5">
            <img src="https://cdn.magicpatterns.com/uploads/oUNBTi9b5rsXiX3m9xY58X/blob-b68f2d9.webp" alt="FIQIS Logo" className="w-28 h-28 md:w-36 md:h-36 object-contain" style={{
            filter: 'hue-rotate(80deg) saturate(1.5) brightness(1.4)',
            mixBlendMode: 'lighten'
          }} data-id="element-14" data-mp="31j929upnhhue" />
          </div>
          <h1 className="font-heading text-[length:var(--title-size)] text-gradient mb-4 animate-on-view tracking-tight" data-id="element-15" data-mp="3fot0bh8nh72l">
            FIQIS
          </h1>
          <p className="text-[length:var(--h3-size)] text-[#f0f4ff] font-medium mb-6 animate-on-view delay-100" data-id="element-16" data-mp="3uc48d95mi8xy">
            Financial Quantum Intelligence Systems
          </p>
          <p className="text-[#00c8ff] text-[length:var(--small-size)] uppercase tracking-widest mb-8 animate-on-view delay-200 font-bold" data-id="element-17" data-mp="77y5mp20aszl">
            Venture-Capital Business Plan
          </p>
          <p className="text-[#7a8ba8] text-[length:var(--small-size)] animate-on-view delay-300" data-id="element-18" data-mp="28zkqs9z7vto5">
            Arthur Miller — Founder & CEO{' '}
            <span className="mx-2 text-[#6366f1]" data-id="element-19" data-mp="250u28r8thkcn">•</span> Mark Youwanes —
            Co-Founder & VP Engineering
          </p>
        </div>

        <div className="print-hide absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-on-view delay-400 text-[#7a8ba8]" data-id="element-20" data-mp="cwobul4bigos">
          <span className="text-sm uppercase tracking-widest" data-id="element-21" data-mp="hydqeaqr1chm">
            Scroll to explore
          </span>
          <ChevronDown className="w-5 h-5 animate-bounce" data-id="element-22" data-mp="mt15jizv4ujj" />
        </div>
      </section>

      {/* Slide 2: Executive Summary (data-index=1) */}
      <section className="slide" data-index="1" data-id="element-23" data-mp="vv0le7z55skw">
        <div className="slide-content text-center w-full" data-id="element-24" data-mp="vtphm85p4kts">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-6 animate-on-view" data-id="element-25" data-mp="10o3igq34nk5u">
            Executive Summary
          </h2>

          <p className="text-[length:var(--body-size)] text-[#f0f4ff] max-w-4xl mx-auto mb-10 animate-on-view delay-100 leading-relaxed" data-id="element-26" data-mp="15npvayf1mye4">
            FIQIS is a financial infrastructure platform designed to help banks
            and credit unions modernize securely while retaining ownership of
            their customer relationships and revenue streams.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-10 w-full relative" data-id="element-27" data-mp="2v1fqreiweca4">
            <div className="hidden md:block absolute top-1/2 left-10 right-10 h-0.5 bg-gradient-to-r from-transparent via-[#00c8ff]/30 to-transparent -translate-y-1/2 z-0" data-id="element-28" data-mp="2q4p9invcxan4"></div>

            {[{
            icon: ShieldCheck,
            text: 'Post-Quantum Secure Integration'
          }, {
            icon: Brain,
            text: 'AI-Driven Financial Intelligence'
          }, {
            icon: Banknote,
            text: 'Bank-Owned Micro-Lending'
          }, {
            icon: Users,
            text: 'Regulated P2P Lending Networks'
          }].map((item, i) => <div key={i} className={`relative z-10 flex items-center gap-3 bg-[#0d1528] border border-[rgba(0,200,255,0.15)] rounded-full px-6 py-4 shadow-lg animate-on-view delay-${(i + 1) * 100} hover:border-[#00c8ff]/50 transition-colors`} data-id="element-29" data-mp="ldtpfxur9eg" data-mpi={i}>
                <item.icon className="w-6 h-6 text-[#00c8ff]" data-id="element-30" data-mp="2w5eg1u3kl7o6" />
                <span className="font-heading font-bold text-[length:var(--small-size)]" data-id="element-31" data-mp="3056u2mbqg60x">
                  {item.text}
                </span>
              </div>)}
          </div>

          <div className="bg-[#111d35]/50 border border-white/5 rounded-xl p-6 max-w-3xl mx-auto animate-on-view delay-400" data-id="element-32" data-mp="3su411tahuged">
            <p className="text-[length:var(--body-size)] text-[#00c8ff] font-medium" data-id="element-33" data-mp="3etjgarl0qai8">
              Targeting 2,500+ institutions globally, generating over $1B in
              annual recurring revenue within ten years.
            </p>
          </div>
        </div>
      </section>

      {/* Slide 3: Industry Problem (data-index=2) */}
      <section className="slide" data-index="2" data-id="element-34" data-mp="28ftynfn2eih0">
        <div className="slide-content items-start w-full" data-id="element-35" data-mp="1zwyxvio5sh08">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-36" data-mp="1zzl49ezbcqgv">
            The Industry Problem
          </h2>

          <div className="grid-responsive w-full" data-id="element-37" data-mp="29umwijovy8no">
            {[{
            icon: LinkIcon,
            title: 'Integration Complexity',
            desc: 'Integrating new products with legacy banking cores takes 6–18 months and creates costly, fragile connections.'
          }, {
            icon: ShieldAlert,
            title: 'Security Evolution',
            desc: 'Quantum computing threatens traditional cryptographic systems. Institutions must prepare for post-quantum migration.'
          }, {
            icon: EyeOff,
            title: 'Data Underutilization',
            desc: 'Banks possess massive volumes of financial data but lack tools to convert it into meaningful insight.'
          }, {
            icon: TrendingDown,
            title: 'Revenue Loss to Fintech',
            desc: 'Companies like Klarna, Affirm, and Afterpay have captured significant portions of the consumer lending market.'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] border-l-4 border-[#00c8ff] p-6 rounded-r-xl shadow-lg animate-on-view transition-transform hover:scale-[1.02] delay-${(i + 1) * 100}`} data-id="element-38" data-mp="1f2flj26txhyt" data-mpi={i}>
                <item.icon className="w-8 h-8 text-[#00c8ff] mb-4" data-id="element-39" data-mp="1692jnaqsf60l" />
                <h3 className="font-heading text-[length:var(--h3-size)] mb-2" data-id="element-40" data-mp="11cu9ycvd050t">
                  {item.title}
                </h3>
                <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-41" data-mp="wav671vpj63c">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Slide 4: Market Opportunity (data-index=3) */}
      <section className="slide" data-index="3" data-id="element-42" data-mp="2yyv1gxhyyzau">
        <div className="slide-content items-start w-full" data-id="element-43" data-mp="33187ln0moz85">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-44" data-mp="32znapqqud474">
            Market Opportunity
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 w-full" data-id="element-45" data-mp="3czxqnj49gppz">
            {[{
            icon: Building2,
            stat: '4,500',
            desc: 'U.S. banks'
          }, {
            icon: Users,
            stat: '4,700',
            desc: 'U.S. credit unions'
          }, {
            icon: Globe,
            stat: '25K+',
            desc: 'Banks globally'
          }, {
            icon: Building2,
            stat: '85K+',
            desc: 'Global financial cooperatives and credit unions'
          }, {
            icon: Target,
            stat: '100K–120K',
            desc: 'Estimated serviceable institutions'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] p-6 rounded-xl border border-white/5 shadow-lg animate-on-view transition-transform hover:scale-[1.02] delay-${(i + 1) * 100}`} data-id="element-46" data-mp="239cubgz0b9wh" data-mpi={i}>
                <item.icon className="w-8 h-8 text-[#00c8ff] mb-4" data-id="element-47" data-mp="1ajx9y0lgc4nw" />
                <h3 className="font-heading text-[length:var(--h2-size)] text-[#00c8ff] mb-2" data-id="element-48" data-mp="16j3m1780epv0">
                  {item.stat}
                </h3>
                <p className="text-[length:var(--small-size)] text-[#7a8ba8]" data-id="element-49" data-mp="1kjo4g2a0ijnz">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Slide 5: Market Drivers (data-index=4) */}
      <section className="slide" data-index="4" data-id="element-50" data-mp="2qz775yjbsygq">
        <div className="slide-content items-start w-full" data-id="element-51" data-mp="2qyfa0ja2ysoc">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-52" data-mp="cqo7i1d8ps25">
            Market Drivers
          </h2>

          <div className="grid md:grid-cols-2 gap-[var(--content-gap)] w-full max-w-5xl mx-auto" data-id="element-53" data-mp="hrk9uahy165g">
            {[{
            icon: TrendingUp,
            title: 'Digital Banking Transformation',
            desc: 'Banks must modernize to compete with fintech companies'
          }, {
            icon: ShieldCheck,
            title: 'Post-Quantum Cryptography',
            desc: 'NIST standardizing quantum-resistant algorithms — institutions must upgrade security'
          }, {
            icon: Brain,
            title: 'Data-Driven Services',
            desc: 'Customers increasingly expect personalized financial services'
          }, {
            icon: Banknote,
            title: 'Embedded Lending',
            desc: 'BNPL services have demonstrated strong consumer demand for flexible credit'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] border-l-4 border-[#00c8ff] p-6 rounded-xl shadow-lg animate-on-view transition-transform hover:scale-[1.02] delay-${(i + 1) * 100}`} data-id="element-54" data-mp="3mnodcr1kv3z0" data-mpi={i}>
                <item.icon className="w-8 h-8 text-[#00c8ff] mb-4" data-id="element-55" data-mp="e9eoeyycrpz1" />
                <h3 className="font-heading text-[length:var(--h3-size)] mb-2" data-id="element-56" data-mp="e8mrnkt0rm76">
                  {item.title}
                </h3>
                <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-57" data-mp="5h4dfffmvggq">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Slide 6: Product Platform (data-index=5) */}
      <section className="slide" data-index="5" data-id="element-58" data-mp="xyyh8zy1onlf">
        <div className="slide-content text-center" data-id="element-59" data-mp="t6ymrn0qumw8">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-4 animate-on-view" data-id="element-60" data-mp="t53zi2202ioz">
            The FIQIS Platform
          </h2>
          <p className="text-[length:var(--h3-size)] text-[#7a8ba8] mb-[var(--content-gap)] animate-on-view delay-100" data-id="element-61" data-mp="15gwrj6o1w2pp">
            Four integrated capabilities delivered as white-label SaaS
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-[var(--content-gap)] w-full relative" data-id="element-62" data-mp="1ehcri65l4rpt">
            <div className="hidden md:block absolute top-1/2 left-10 right-10 h-0.5 bg-gradient-to-r from-transparent via-[#00c8ff]/30 to-transparent -translate-y-1/2 z-0" data-id="element-63" data-mp="1jgz6ak12ag0q"></div>

            {[{
            icon: ShieldCheck,
            text: 'Post-Quantum Security'
          }, {
            icon: Brain,
            text: 'AI Intelligence'
          }, {
            icon: Banknote,
            text: 'Micro-Lending'
          }, {
            icon: Users,
            text: 'P2P Lending'
          }].map((item, i) => <div key={i} className={`relative z-10 flex items-center gap-3 bg-[#0d1528] border border-[rgba(0,200,255,0.15)] rounded-full px-6 py-4 shadow-lg animate-on-view delay-${(i + 1) * 100} hover:border-[#00c8ff]/50 transition-colors`} data-id="element-64" data-mp="74acx8mt4ynw" data-mpi={i}>
                <item.icon className="w-6 h-6 text-[#00c8ff]" data-id="element-65" data-mp="c1kcvi7tospz" />
                <span className="font-heading font-bold text-[length:var(--body-size)]" data-id="element-66" data-mp="qr8d0g49plc4">
                  {item.text}
                </span>
              </div>)}
          </div>

          <p className="text-[length:var(--body-size)] text-[#f0f4ff] max-w-3xl mx-auto animate-on-view delay-400 bg-[#111d35]/50 p-6 rounded-xl border border-white/5" data-id="element-67" data-mp="zt7vzdyuezhp">
            FIQIS enables financial institutions to deploy modern services
            within their own digital channels while maintaining regulatory
            compliance and brand ownership.
          </p>
        </div>
      </section>

      {/* Slide 7: Pillar 1 - Post-Quantum Security (data-index=6) */}
      <section className="slide" data-index="6" data-id="element-68" data-mp="1h20omdyz4u25">
        <div className="slide-content flex-row flex-wrap md:flex-nowrap items-center gap-[var(--content-gap)]" data-id="element-69" data-mp="1h0p95gw61xv7">
          <div className="flex-1 w-full animate-on-view" data-id="element-70" data-mp="13a3yugks2w8x">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-2" data-id="element-71" data-mp="17bp69mejmibq">
              Pillar 1: Secure Integration Layer
            </h2>
            <p className="text-[length:var(--h3-size)] text-[#00c8ff] mb-8 font-heading" data-id="element-72" data-mp="tav07aeywtg8">
              Post-Quantum Ready
            </p>

            <ul className="space-y-6" data-id="element-73" data-mp="kbrgq5bw2c17">
              {[{
              icon: Lock,
              text: 'Standardized financial APIs'
            }, {
              icon: Shield,
              text: 'Secure integration gateway'
            }, {
              icon: Server,
              text: 'Fintech onboarding infrastructure'
            }, {
              icon: ShieldCheck,
              text: 'Post-quantum cryptographic readiness'
            }].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-74" data-mp="wrxvz9g9cx1z" data-mpi={i}>
                  <div className="mt-1 bg-[#111d35] p-2 rounded-lg border border-[#00c8ff]/20" data-id="element-75" data-mp="wq4mlme9fyqa">
                    <item.icon className="w-6 h-6 text-[#00c8ff]" data-id="element-76" data-mp="rr1sgwztjcx3" />
                  </div>
                  <span className="text-[length:var(--body-size)] text-[#f0f4ff] leading-relaxed" data-id="element-77" data-mp="nsuajytmc5kb">
                    {item.text}
                  </span>
                </li>)}
            </ul>
          </div>

          <div className="flex-1 w-full flex justify-center animate-on-view delay-200" data-id="element-78" data-mp="dv6caywt0fxe">
            <div className="relative w-64 h-64 md:w-80 md:h-80" data-id="element-79" data-mp="8wcx921vcgv4">
              <div className="absolute inset-0 border border-[#00c8ff]/30 rotate-45 rounded-2xl animate-pulse-glow" data-id="element-80" data-mp="286332bbrpn1j"></div>
              <div className="absolute inset-4 border border-[#6366f1]/40 rotate-[60deg] rounded-2xl animate-spin-slow" data-id="element-81" data-mp="2392xs2mqsobh"></div>
              <div className="absolute inset-8 border border-[#8b5cf6]/50 rotate-45 rounded-2xl bg-[#111d35]/80 backdrop-blur-md flex items-center justify-center shadow-[0_0_30px_rgba(0,200,255,0.2)]" data-id="element-82" data-mp="2fqvpznejpioo">
                <ShieldCheck className="w-16 h-16 text-[#00c8ff]" data-id="element-83" data-mp="2pgozmiv4nnqa" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 8: Pillar 2 - AI Intelligence (data-index=7) */}
      <section className="slide" data-index="7" data-id="element-84" data-mp="1wrhs4udp3nsw">
        <div className="slide-content flex-row flex-wrap md:flex-nowrap items-center gap-[var(--content-gap)]" data-id="element-85" data-mp="1wsb2e5bv4vfs">
          <div className="flex-1 w-full flex justify-center order-2 md:order-1 animate-on-view delay-200" data-id="element-86" data-mp="21t73kb4ty3hi">
            <div className="relative w-64 h-64 md:w-80 md:h-80" data-id="element-87" data-mp="26nuo5ywpqdqt">
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" data-id="element-88" data-mp="3d5rghohxtcjq">
                <line x1="50" y1="20" x2="20" y2="70" stroke="rgba(0,200,255,0.2)" strokeWidth="1" data-id="element-89" data-mp="3i770recajf12" />
                <line x1="50" y1="20" x2="80" y2="70" stroke="rgba(99,102,241,0.2)" strokeWidth="1" data-id="element-90" data-mp="17k34fu4zn5a4" />
                <line x1="20" y1="70" x2="80" y2="70" stroke="rgba(139,92,246,0.2)" strokeWidth="1" data-id="element-91" data-mp="ex8wtxt9s0e2" />
              </svg>
              <div className="absolute top-[10%] left-1/2 -translate-x-1/2 w-16 h-16 rounded-full border border-[#00c8ff] bg-[#0d1528] flex items-center justify-center z-10 animate-float shadow-[0_0_15px_rgba(0,200,255,0.3)]" data-id="element-92" data-mp="owhmieh3qsum">
                <Brain className="w-8 h-8 text-[#00c8ff]" data-id="element-93" data-mp="24acby1o5vlaj" />
              </div>
              <div className="absolute bottom-[20%] left-[10%] w-12 h-12 rounded-full border border-[#6366f1] bg-[#0d1528] flex items-center justify-center z-10 animate-float-delayed" data-id="element-94" data-mp="3oswlvk2izmiz">
                <Scan className="w-6 h-6 text-[#6366f1]" data-id="element-95" data-mp="3ktox1nm97k6u" />
              </div>
              <div className="absolute bottom-[20%] right-[10%] w-12 h-12 rounded-full border border-[#8b5cf6] bg-[#0d1528] flex items-center justify-center z-10 animate-float" data-id="element-96" data-mp="3fyrr4pk9tqy6">
                <Target className="w-6 h-6 text-[#8b5cf6]" data-id="element-97" data-mp="3637x68p9p2c2" />
              </div>
            </div>
          </div>

          <div className="flex-1 w-full order-1 md:order-2 animate-on-view" data-id="element-98" data-mp="311scwiuwyzuq">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-8" data-id="element-99" data-mp="2x2rtay4m3t6o">
              Pillar 2: AI-Driven Financial Intelligence
            </h2>
            <ul className="space-y-6" data-id="element-100" data-mp="2s505af738r7r">
              {[{
              icon: Scan,
              text: 'Customer cash-flow modeling'
            }, {
              icon: Sparkles,
              text: 'Behavioral financial insights'
            }, {
              icon: Brain,
              text: 'Explainable AI product recommendations'
            }, {
              icon: Target,
              text: 'Trust scoring models'
            }].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-101" data-mp="15bxvnar69t8s" data-mpi={i}>
                  <div className="mt-1 bg-[#111d35] p-2 rounded-lg border border-[#6366f1]/20" data-id="element-102" data-mp="1t57mertfiksv">
                    <item.icon className="w-6 h-6 text-[#6366f1]" data-id="element-103" data-mp="1x46tahyq0rmy" />
                  </div>
                  <span className="text-[length:var(--body-size)] text-[#f0f4ff] leading-relaxed" data-id="element-104" data-mp="1j4nmbshnbmc8">
                    {item.text}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Slide 9: Pillar 3 - Micro-Lending (data-index=8) */}
      <section className="slide" data-index="8" data-id="element-105" data-mp="cr7qrt1m9hbf">
        <div className="slide-content flex-row flex-wrap md:flex-nowrap items-center gap-[var(--content-gap)]" data-id="element-106" data-mp="55vkcvtjip85">
          <div className="flex-1 w-full animate-on-view" data-id="element-107" data-mp="7kc2piw8ms1">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-8" data-id="element-108" data-mp="9570g94e7qf">
              Pillar 3: Micro-Lending Infrastructure
            </h2>

            <ul className="space-y-6 mb-8" data-id="element-109" data-mp="97h3gywo3zqs">
              {['Point-of-sale installment lending', 'Small balance consumer loans', 'In-app financing'].map((text, i) => <li key={i} className="flex items-start gap-4" data-id="element-110" data-mp="3dnrxwenfyu3f" data-mpi={i}>
                  <div className="mt-1 bg-[#111d35] p-2 rounded-lg border border-[#00c8ff]/20" data-id="element-111" data-mp="3redkoeg0no6l">
                    <Banknote className="w-6 h-6 text-[#00c8ff]" data-id="element-112" data-mp="3mmdh8c0qmd59" />
                  </div>
                  <span className="text-[length:var(--body-size)] text-[#f0f4ff] leading-relaxed" data-id="element-113" data-mp="2ywj55wo8ssm2">
                    {text}
                  </span>
                </li>)}
            </ul>

            <div className="bg-[#111d35]/50 border-l-4 border-[#00c8ff] p-4 rounded-r-lg" data-id="element-114" data-mp="37lgatppavlrj">
              <p className="text-[length:var(--body-size)] text-[#00c8ff]" data-id="element-115" data-mp="3cj8cvcjnso9g">
                Enables banks to reclaim revenue currently captured by fintech
                companies.
              </p>
            </div>
          </div>

          <div className="flex-1 w-full flex justify-center animate-on-view delay-200" data-id="element-116" data-mp="3mb4aynlqwedo">
            <div className="relative w-64 h-64 md:w-80 md:h-80 flex items-center justify-center" data-id="element-117" data-mp="3qad3xq8yo886">
              <div className="absolute w-48 h-32 bg-[#111d35] border border-[#00c8ff]/30 rounded-xl transform -rotate-12 -translate-y-8 opacity-50 shadow-lg" data-id="element-118" data-mp="3v8y63v7rx1z9"></div>
              <div className="absolute w-48 h-32 bg-[#111d35] border border-[#6366f1]/40 rounded-xl transform -rotate-6 -translate-y-4 opacity-75 shadow-lg" data-id="element-119" data-mp="92rghaote5ra"></div>
              <div className="absolute w-48 h-32 bg-[#111d35] border border-[#8b5cf6]/50 rounded-xl flex items-center justify-center shadow-[0_0_30px_rgba(0,200,255,0.2)] backdrop-blur-sm z-10 animate-float" data-id="element-120" data-mp="25xe5pdiw3k2g">
                <Banknote className="w-16 h-16 text-[#00c8ff]" data-id="element-121" data-mp="s14gjdbwep7v" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 10: Pillar 4 - P2P Lending (data-index=9) */}
      <section className="slide" data-index="9" data-id="element-122" data-mp="j4lsofiixri5">
        <div className="slide-content flex-row flex-wrap md:flex-nowrap items-center gap-[var(--content-gap)]" data-id="element-123" data-mp="e96dnk8s5pk5">
          <div className="flex-1 w-full flex justify-center order-2 md:order-1 animate-on-view delay-200" data-id="element-124" data-mp="1axm5jmzd4b0f">
            <div className="relative w-64 h-64 md:w-80 md:h-80 flex items-center justify-center" data-id="element-125" data-mp="16xby05vx9cjg">
              <div className="absolute inset-0 border-2 border-dashed border-[#6366f1]/30 rounded-full animate-spin-slow" data-id="element-126" data-mp="11vmmyq8p8yvd"></div>
              <div className="absolute top-4 left-1/2 -translate-x-1/2 w-12 h-12 bg-[#111d35] border border-[#6366f1] rounded-full flex items-center justify-center shadow-lg" data-id="element-127" data-mp="3mnoqdz9o1ta3">
                <Users className="w-6 h-6 text-[#6366f1]" data-id="element-128" data-mp="3hqokppgqasji" />
              </div>
              <div className="absolute bottom-10 left-4 w-12 h-12 bg-[#111d35] border border-[#00c8ff] rounded-full flex items-center justify-center shadow-lg" data-id="element-129" data-mp="3j6ah577cu1x8">
                <Users className="w-6 h-6 text-[#00c8ff]" data-id="element-130" data-mp="3a5m1y9685vir" />
              </div>
              <div className="absolute bottom-10 right-4 w-12 h-12 bg-[#111d35] border border-[#8b5cf6] rounded-full flex items-center justify-center shadow-lg" data-id="element-131" data-mp="3f45q8g8ajjea">
                <Users className="w-6 h-6 text-[#8b5cf6]" data-id="element-132" data-mp="30dq674rm2hhp" />
              </div>
              <div className="w-20 h-20 bg-[#0d1528] border-2 border-[#6366f1] rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(99,102,241,0.4)] z-10" data-id="element-133" data-mp="3571aoz8yam6g">
                <ShieldCheck className="w-10 h-10 text-[#6366f1]" data-id="element-134" data-mp="2vfol6eamk83l" />
              </div>
            </div>
          </div>

          <div className="flex-1 w-full order-1 md:order-2 animate-on-view" data-id="element-135" data-mp="2hhgh7mf686ix">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-2" data-id="element-136" data-mp="2mg1i7o28yu8f">
              Pillar 4: Peer-to-Peer Lending
            </h2>
            <p className="text-[length:var(--h3-size)] text-[#6366f1] mb-8 font-heading" data-id="element-137" data-mp="22uhv86z324bl">
              Regulated lending within financial institutions
            </p>

            <ul className="space-y-6 mb-8" data-id="element-138" data-mp="2csfhioxdtr1k">
              {[{
              icon: Shield,
              text: 'Regulatory oversight'
            }, {
              icon: Target,
              text: 'Risk management'
            }, {
              icon: Server,
              text: 'Loan servicing infrastructure'
            }].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-139" data-mp="2vkgka2da21g9" data-mpi={i}>
                  <div className="mt-1 bg-[#111d35] p-2 rounded-lg border border-[#6366f1]/20" data-id="element-140" data-mp="2qkluy395hjxb">
                    <item.icon className="w-6 h-6 text-[#6366f1]" data-id="element-141" data-mp="34k3loo60b79b" />
                  </div>
                  <span className="text-[length:var(--body-size)] text-[#f0f4ff] leading-relaxed" data-id="element-142" data-mp="30ii44pij24ss">
                    {item.text}
                  </span>
                </li>)}
            </ul>

            <div className="bg-[#111d35]/50 border-l-4 border-[#6366f1] p-4 rounded-r-lg" data-id="element-143" data-mp="fotxmzio4zcy">
              <p className="text-[length:var(--body-size)] text-[#6366f1]" data-id="element-144" data-mp="kq9j2sord7vv">
                Creates an entirely new lending market within banking
                ecosystems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 11: Technology Architecture (data-index=10) */}
      <section className="slide" data-index="10" data-id="element-145" data-mp="3t5jgvolm7qxu">
        <div className="slide-content items-start w-full" data-id="element-146" data-mp="11h9r45e2hje">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-2 animate-on-view" data-id="element-147" data-mp="zxtvenna8i5">
            Technology Architecture
          </h2>
          <p className="text-[length:var(--h3-size)] text-[#7a8ba8] mb-[var(--content-gap)] animate-on-view delay-100" data-id="element-148" data-mp="35fp394th1ycj">
            Cloud-native platform built for enterprise reliability
          </p>

          <div className="grid md:grid-cols-2 gap-[var(--content-gap)] w-full max-w-5xl mx-auto mb-8" data-id="element-149" data-mp="31x9n8kh6g6nr">
            {[{
            icon: Server,
            title: 'Microservices Infrastructure'
          }, {
            icon: Layers,
            title: 'Event-Driven Data Pipelines'
          }, {
            icon: Brain,
            title: 'Distributed Analytics Engines'
          }, {
            icon: Lock,
            title: 'Secure Financial APIs'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] border border-white/5 p-6 rounded-xl shadow-lg flex items-center gap-4 animate-on-view transition-transform hover:scale-[1.02] delay-${(i + 1) * 100}`} data-id="element-150" data-mp="2982oplhq3h2i" data-mpi={i}>
                <div className="bg-[#0d1528] p-3 rounded-lg border border-[#00c8ff]/20" data-id="element-151" data-mp="20a0ecfc3nc2k">
                  <item.icon className="w-8 h-8 text-[#00c8ff]" data-id="element-152" data-mp="1vb5y9l5io1m5" />
                </div>
                <h3 className="font-heading text-[length:var(--h3-size)] text-white" data-id="element-153" data-mp="145p175ucobjy">
                  {item.title}
                </h3>
              </div>)}
          </div>

          <p className="text-center w-full text-[length:var(--small-size)] text-[#7a8ba8] animate-on-view delay-400" data-id="element-154" data-mp="1e44thc545qv1">
            Deployed on AWS with enterprise-grade reliability
          </p>
        </div>
      </section>

      {/* Slide 12: Competitive Landscape (data-index=11) */}
      <section className="slide" data-index="11" data-id="element-155" data-mp="giqmaclvpbo5">
        <div className="slide-content items-start w-full" data-id="element-156" data-mp="gfunyn64ckz6">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-157" data-mp="v7dal3thhdri">
            Competitive Landscape
          </h2>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 w-full mb-[var(--content-gap)]" data-id="element-158" data-mp="2q0z44um60pg">
            {[{
            name: 'Plaid',
            desc: 'Financial data connectivity'
          }, {
            name: 'nCino',
            desc: 'Banking software platform'
          }, {
            name: 'equipifi',
            desc: 'BNPL for credit unions'
          }, {
            name: 'Jifiti',
            desc: 'Embedded lending infrastructure'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] p-6 rounded-xl border border-white/5 shadow-lg animate-on-view delay-${(i + 1) * 100}`} data-id="element-159" data-mp="2s4sthitexhxp" data-mpi={i}>
                <h3 className="font-heading text-[length:var(--h3-size)] text-white mb-2" data-id="element-160" data-mp="2j4vyz0kcdouq">
                  {item.name}
                </h3>
                <p className="text-[length:var(--small-size)] text-[#7a8ba8]" data-id="element-161" data-mp="2o1w4999danks">
                  {item.desc}
                </p>
              </div>)}
          </div>

          <div className="w-full bg-[#111d35] rounded-xl p-8 border-gradient-top shadow-xl animate-on-view delay-400 text-center" data-id="element-162" data-mp="1cpbq5skix8gz">
            <h3 className="font-heading text-[length:var(--h3-size)] text-gradient mb-4" data-id="element-163" data-mp="3ecoizfzdecf1">
              The FIQIS Differentiation
            </h3>
            <p className="text-[length:var(--body-size)] text-[#f0f4ff] max-w-3xl mx-auto" data-id="element-164" data-mp="34n4t3qd8piao">
              FIQIS combines secure integration infrastructure, post-quantum
              readiness, AI-driven analytics, and lending infrastructure in a
              single unified platform.
            </p>
          </div>
        </div>
      </section>

      {/* Slide 13: Business Model (data-index=12) */}
      <section className="slide" data-index="12" data-id="element-165" data-mp="2lo954wrz605a">
        <div className="slide-content w-full" data-id="element-166" data-mp="2lqv8slbqvvi8">
          <div className="text-center mb-[var(--content-gap)] animate-on-view" data-id="element-167" data-mp="2hnyxlfojb1ob">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-2" data-id="element-168" data-mp="jx5rdymsg5gi">
              Business Model
            </h2>
            <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-169" data-mp="fvkjkrp42hd6">
              High-margin SaaS platform
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-[var(--content-gap)] w-full max-w-5xl mx-auto" data-id="element-170" data-mp="1xrct9mk0hla2">
            <div className="bg-[#111d35] rounded-xl p-10 border-gradient-top shadow-xl animate-on-view delay-100 flex flex-col items-center text-center hover:scale-[1.02] transition-transform" data-id="element-171" data-mp="22qfnebyge739">
              <DollarSign className="w-12 h-12 text-[#00c8ff] mb-6" data-id="element-172" data-mp="2bk2idzuu76be" />
              <h3 className="font-heading text-[length:var(--h3-size)] mb-6 text-white" data-id="element-173" data-mp="1evmm7kx81ypb">
                SaaS Licensing
              </h3>
              <div className="font-heading text-[length:var(--title-size)] text-gradient leading-none mb-4" data-id="element-174" data-mp="1jvsbiug0r948">
                $300,000
              </div>
              <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-175" data-mp="1svfa78hd60tl">
                per institution per year
              </p>
            </div>

            <div className="bg-[#111d35] rounded-xl p-10 border-gradient-top shadow-xl animate-on-view delay-200 flex flex-col items-center text-center hover:scale-[1.02] transition-transform" data-id="element-176" data-mp="2zcsse3j6jfj1">
              <BarChart3 className="w-12 h-12 text-[#6366f1] mb-6" data-id="element-177" data-mp="32u6xqkidemqm" />
              <h3 className="font-heading text-[length:var(--h3-size)] mb-6 text-white" data-id="element-178" data-mp="2xuu2pchci9cy">
                Transaction Revenue
              </h3>
              <div className="font-heading text-[length:var(--title-size)] text-gradient leading-none mb-4" data-id="element-179" data-mp="37rgoc3hhx4g1">
                $100K–$300K
              </div>
              <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-180" data-mp="3lpnbopotjm1g">
                additional per institution annually
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 14: Go-to-Market (data-index=13) */}
      <section className="slide" data-index="13" data-id="element-181" data-mp="3qheuuf5naphk">
        <div className="slide-content items-start w-full" data-id="element-182" data-mp="3qk1az36lqdbe">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] animate-on-view" data-id="element-183" data-mp="8dd6nfjdlu6s">
            Go-to-Market Strategy
          </h2>

          <div className="grid md:grid-cols-2 gap-[var(--content-gap)] w-full max-w-5xl mx-auto" data-id="element-184" data-mp="mz0t57d1xast">
            {[{
            icon: Handshake,
            title: 'Direct Sales',
            desc: 'Direct sales to financial institutions'
          }, {
            icon: Building2,
            title: 'Strategic Partnerships',
            desc: 'FIS, Fiserv, Jack Henry, digital banking providers'
          }, {
            icon: Users,
            title: 'Channel Partners',
            desc: 'Fintech consultants and banking technology integrators'
          }, {
            icon: Rocket,
            title: 'Pilot Programs',
            desc: 'Early adoption with discount incentives'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] border-l-4 border-[#6366f1] p-6 rounded-xl shadow-lg animate-on-view transition-transform hover:scale-[1.02] delay-${(i + 1) * 100}`} data-id="element-185" data-mp="3hfksaj7fxm4b" data-mpi={i}>
                <item.icon className="w-8 h-8 text-[#6366f1] mb-4" data-id="element-186" data-mp="1n778q5chvfu9" />
                <h3 className="font-heading text-[length:var(--h3-size)] mb-2" data-id="element-187" data-mp="1n5w4k4f5062m">
                  {item.title}
                </h3>
                <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-188" data-mp="1e6iv3br0jhhw">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Slide 15: Growth & Revenue (data-index=14) */}
      <section className="slide" data-index="14" data-id="element-189" data-mp="25y84kkibiv9l">
        <div className="slide-content w-full" data-id="element-190" data-mp="21vv3i9einlj5">
          <div className="text-center mb-[var(--content-gap)] animate-on-view" data-id="element-191" data-mp="21ujzq9l2mds1">
            <h2 className="font-heading text-[length:var(--h2-size)] mb-2" data-id="element-192" data-mp="1ww8mrpzhbn5l">
              Growth Trajectory
            </h2>
            <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-193" data-mp="qi9dkm0u1fwk">
              10-Year Customer & Revenue Projection
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 w-full max-w-6xl mx-auto mb-12" data-id="element-194" data-mp="2x5zygi0vxj70">
            {[{
            year: 'Year 1',
            rev: '$300K',
            inst: '1 institution'
          }, {
            year: 'Year 3',
            rev: '$8M',
            inst: '20 institutions'
          }, {
            year: 'Year 5',
            rev: '$80M',
            inst: '200 institutions'
          }, {
            year: 'Year 7',
            rev: '$360M',
            inst: '800 institutions'
          }, {
            year: 'Year 10',
            rev: '$1B+',
            inst: '2,500 institutions'
          }].map((item, i) => <div key={i} className={`bg-[#111d35] rounded-xl p-5 border-gradient-top shadow-lg animate-on-view delay-${(i + 1) * 100}`} data-id="element-195" data-mp="1vhfq2p9ea30i" data-mpi={i}>
                <p className="text-[length:var(--small-size)] text-[#7a8ba8] mb-2 uppercase tracking-wider" data-id="element-196" data-mp="1gjwp8c57eks4">
                  {item.year}
                </p>
                <h3 className="font-heading text-[length:var(--h3-size)] text-gradient mb-2" data-id="element-197" data-mp="1pd9rw5t2l0re">
                  {item.rev}
                </h3>
                <p className="text-[length:var(--small-size)] text-[#7a8ba8]" data-id="element-198" data-mp="1ld7mblk7rn6u">
                  {item.inst}
                </p>
              </div>)}
          </div>

          {/* CSS Growth Bar */}
          <div className="w-full max-w-6xl mx-auto relative h-12 animate-on-view delay-500" data-id="element-199" data-mp="sotrtfsc3901">
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#00c8ff] to-[#6366f1] -translate-y-1/2 rounded-full" data-id="element-200" data-mp="16ol85n6bp1h7"></div>
            <div className="absolute top-1/2 left-[0%] w-3 h-3 bg-[#00c8ff] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_10px_rgba(0,200,255,0.8)]" data-id="element-201" data-mp="2i1x5rgbhonux"></div>
            <div className="absolute top-1/2 left-[25%] w-4 h-4 bg-[#21a6f6] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_10px_rgba(33,166,246,0.8)]" data-id="element-202" data-mp="2d56qgv7idwak"></div>
            <div className="absolute top-1/2 left-[50%] w-5 h-5 bg-[#4286ed] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_10px_rgba(66,134,237,0.8)]" data-id="element-203" data-mp="2pmhix9dfxub3"></div>
            <div className="absolute top-1/2 left-[75%] w-6 h-6 bg-[#6366f1] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_15px_rgba(99,102,241,0.8)]" data-id="element-204" data-mp="2gkhzkaeyee4z"></div>
            <div className="absolute top-1/2 left-[100%] w-7 h-7 bg-[#8b5cf6] rounded-full -translate-y-1/2 -translate-x-1/2 shadow-[0_0_20px_rgba(139,92,246,0.8)]" data-id="element-205" data-mp="34cyj6pecae6e"></div>
          </div>
        </div>
      </section>

      {/* Slide 16: Funding Plan (data-index=15) */}
      <section className="slide" data-index="15" data-id="element-206" data-mp="2zib39exeis3f">
        <div className="slide-content text-center" data-id="element-207" data-mp="3n9q93hodzrjf">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-8 animate-on-view" data-id="element-208" data-mp="3n7vl1uhtjjb4">
            Funding Plan
          </h2>

          <div className="mb-12 animate-on-view delay-100" data-id="element-209" data-mp="ulrie9vnjxly">
            <div className="font-heading text-[length:var(--title-size)] text-gradient leading-none mb-4" data-id="element-210" data-mp="uk824j9zxmk6">
              $3M
            </div>
            <div className="text-[#00c8ff] font-heading text-[length:var(--h3-size)]" data-id="element-211" data-mp="ll35xjlwq5b6">
              Seed Round
            </div>
          </div>

          <div className="bg-[#111d35]/40 border border-white/5 rounded-2xl p-8 max-w-4xl mx-auto w-full animate-on-view delay-200" data-id="element-212" data-mp="qkz7zdb1nmo3">
            <p className="text-[length:var(--small-size)] text-[#7a8ba8] uppercase tracking-widest mb-6" data-id="element-213" data-mp="1ebv58py9ra2q">
              Use of Funds
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-214" data-mp="14kqqnrci9r8f">
              {[{
              icon: Server,
              label: 'Engineering Team'
            }, {
              icon: Brain,
              label: 'Platform Development'
            }, {
              icon: ShieldCheck,
              label: 'Security & Compliance'
            }, {
              icon: Users,
              label: 'Customer Onboarding & Sales'
            }].map((item, i) => <div key={i} className="flex flex-col items-center gap-3" data-id="element-215" data-mp="2l2wqi3bevcgm" data-mpi={i}>
                  <div className="bg-[#111d35] p-4 rounded-full border border-white/5" data-id="element-216" data-mp="2g54x14v4biab">
                    <item.icon className="w-6 h-6 text-[#6366f1]" data-id="element-217" data-mp="1sfalcqmjbzrn" />
                  </div>
                  <span className="text-[length:var(--small-size)] text-[#7a8ba8] text-center" data-id="element-218" data-mp="1odp3srz22xb4">
                    {item.label}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Slide 17: Exit Opportunities (data-index=16) */}
      <section className="slide" data-index="16" data-id="element-219" data-mp="3dsdf0pvwu97k">
        <div className="slide-content w-full" data-id="element-220" data-mp="3dr2dyxtunfk5">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] text-center animate-on-view" data-id="element-221" data-mp="38tsppli759xw">
            Exit Opportunities
          </h2>

          <div className="max-w-4xl mx-auto w-full space-y-6" data-id="element-222" data-mp="3gwb6ehjez5o2">
            <div className="bg-[#111d35]/40 border border-white/5 rounded-2xl p-8 text-center animate-on-view delay-100" data-id="element-223" data-mp="3kusg98u7yu96">
              <h3 className="font-heading text-[length:var(--h3-size)] text-white mb-6" data-id="element-224" data-mp="3pvpujac3z45e">
                Strategic Acquisition
              </h3>
              <div className="flex flex-wrap justify-center gap-4" data-id="element-225" data-mp="2x6iug6x0a6i1">
                {['Fiserv', 'FIS', 'Jack Henry', 'Visa', 'Mastercard'].map((company, i) => <div key={i} className="bg-[#0d1528] border border-[#00c8ff]/30 px-6 py-3 rounded-full text-[#f0f4ff] font-medium shadow-lg" data-id="element-226" data-mp="374f8b6hbvfju" data-mpi={i}>
                      {company}
                    </div>)}
              </div>
            </div>

            <div className="bg-[#111d35]/40 border border-white/5 rounded-2xl p-8 text-center animate-on-view delay-200" data-id="element-227" data-mp="2jdt09f7bpv9u">
              <h3 className="font-heading text-[length:var(--h3-size)] text-white mb-4" data-id="element-228" data-mp="2buk1efwg1ez1">
                Public Markets
              </h3>
              <p className="text-[length:var(--body-size)] text-[#7a8ba8]" data-id="element-229" data-mp="21wvteo9tuayx">
                IPO pathway once revenue exceeds{' '}
                <span className="text-[#00c8ff] font-bold" data-id="element-230" data-mp="1wx13amxvlpex">$500M ARR</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 18: Team (data-index=17) */}
      <section className="slide" data-index="17" data-id="element-231" data-mp="1o4pojyc4jqes">
        <div className="slide-content w-full" data-id="element-232" data-mp="1o3wd4k280cqb">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-[var(--content-gap)] text-center animate-on-view" data-id="element-233" data-mp="1j7pj9pn3mroq">
            Leadership
          </h2>

          <div className="grid md:grid-cols-2 gap-[var(--content-gap)] w-full max-w-5xl mx-auto" data-id="element-234" data-mp="198gqv17vtl4h">
            {/* Arthur */}
            <div className="bg-[#111d35] rounded-xl p-6 border border-white/5 animate-on-view delay-100 hover:scale-[1.02] transition-transform" data-id="element-235" data-mp="156mtp2wwaz92">
              <div className="flex items-center gap-4 mb-4" data-id="element-236" data-mp="1tyaulig60gxe">
                <img src="https://cdn.magicpatterns.com/uploads/5eYi6BbYwXkeJpk39ZMMtb/image.png" alt="Arthur Miller" className="w-16 h-16 shrink-0 rounded-full object-cover ring-2 ring-[#00c8ff]/60 shadow-lg" data-id="element-237" data-mp="1yvkiuurtimjn" />
                <div data-id="element-238" data-mp="1b6i3jtknp5sb">
                  <h3 className="font-heading text-[length:var(--h3-size)] text-white" data-id="element-239" data-mp="1b6030kqyo5el">
                    Arthur Miller
                  </h3>
                  <p className="text-[#00c8ff] font-medium text-[length:var(--small-size)] uppercase tracking-wider" data-id="element-240" data-mp="1g3rs77081df3">
                    Founder, CEO, CTO & CISO
                  </p>
                  <a href="https://www.linkedin.com/in/arthur-miller-64b32165/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[length:var(--small-size)] text-[#7a8ba8] hover:text-[#00c8ff] transition-colors mt-1" data-id="element-241" data-mp="16a14i4bx570g">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" data-id="element-242" data-mp="ky28q5jz21dr">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-243" data-mp="tx7638jsrooc" />
                    </svg>
                    LinkedIn
                  </a>
                </div>
              </div>
              <p className="text-[#f0f4ff] mb-4 text-[length:var(--body-size)] italic border-l-2 border-[#6366f1] pl-4" data-id="element-244" data-mp="3efl80g3bri1b">
                "Technology executive and enterprise architect with deep
                experience building secure financial infrastructure"
              </p>
              <ul className="space-y-3" data-id="element-245" data-mp="1ndmc1619f34i">
                {['Key architect of Zelle® and Real-Time Payments infrastructure', 'Former CTO of fintech BNPL platform for financial institutions', 'Engineering experience with U.S. Defense Information Systems Agency (DISA)'].map((text, i) => <li key={i} className="flex items-start gap-2 text-[#7a8ba8] text-[length:var(--small-size)]" data-id="element-246" data-mp="2692yy0porcrh" data-mpi={i}>
                    <span className="text-[#00c8ff] mt-1" data-id="element-247" data-mp="2g0p3xfbfkfls">•</span>
                    <span data-id="element-248" data-mp="2g20a1lsclzg2">{text}</span>
                  </li>)}
              </ul>
            </div>

            {/* Mark */}
            <div className="bg-[#111d35] rounded-xl p-6 border border-white/5 animate-on-view delay-200 hover:scale-[1.02] transition-transform" data-id="element-249" data-mp="2p03wckb5r08x">
              <div className="flex items-center gap-4 mb-4" data-id="element-250" data-mp="dbfh7dctmcgm">
                <img src="https://cdn.magicpatterns.com/uploads/vu4yz1jKNzwVywoFhMA84D/image.png" alt="Mark Youwanes" className="w-16 h-16 shrink-0 rounded-full object-cover ring-2 ring-[#00c8ff]/60 shadow-lg" data-id="element-251" data-mp="8ddxqrbhgb4m" />
                <div data-id="element-252" data-mp="w4jd75dih1ee">
                  <h3 className="font-heading text-[length:var(--h3-size)] text-white" data-id="element-253" data-mp="w2q65oyzkf5v">
                    Mark Youwanes
                  </h3>
                  <p className="text-[#00c8ff] font-medium text-[length:var(--small-size)] uppercase tracking-wider" data-id="element-254" data-mp="s4si4pgybz42">
                    Co-Founder & VP Eng
                  </p>
                  <a href="https://www.linkedin.com/in/mark-youwanes-0b62898/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[length:var(--small-size)] text-[#7a8ba8] hover:text-[#00c8ff] transition-colors mt-1" data-id="element-255" data-mp="icd1bvhe5gkr">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" data-id="element-256" data-mp="o4pefc8n86gz">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-257" data-mp="eecw7qoawpe0" />
                    </svg>
                    LinkedIn
                  </a>
                </div>
              </div>
              <p className="text-[#f0f4ff] mb-4 text-[length:var(--body-size)] italic border-l-2 border-[#6366f1] pl-4" data-id="element-258" data-mp="3hvd00fb66dvy">
                "Software engineering leader specializing in scalable
                distributed systems and cloud infrastructure"
              </p>
              <ul className="space-y-3" data-id="element-259" data-mp="35chdsdqqz0ym">
                {['Leads platform engineering and technology execution at FIQIS', 'Expert in cloud-native and microservice architectures', 'Deep experience in secure API and distributed systems'].map((text, i) => <li key={i} className="flex items-start gap-2 text-[#7a8ba8] text-[length:var(--small-size)]" data-id="element-260" data-mp="2mm9haehjz8re" data-mpi={i}>
                    <span className="text-[#00c8ff] mt-1" data-id="element-261" data-mp="31d8kf9zmd53l">•</span>
                    <span data-id="element-262" data-mp="2wje1w9vr2w63">{text}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Board & Advisors */}
          <div className="w-full max-w-5xl mx-auto mt-[var(--content-gap)] animate-on-view delay-300" data-id="element-263" data-mp="23w01g1sufih8">
            <p className="text-[length:var(--small-size)] text-[#7a8ba8] uppercase tracking-widest mb-3 text-center" data-id="element-264" data-mp="13bzh0w5ruq2g">
              Board & Advisors
            </p>
            <div className="bg-[#111d35] rounded-xl p-6 border border-[#8b5cf6]/20 flex flex-col sm:flex-row items-center sm:items-start gap-5 hover:scale-[1.01] transition-transform" data-id="element-265" data-mp="yjg29vvclow2">
              <img src="https://cdn.magicpatterns.com/uploads/hrn4mJoxNSbLUEXiV1mCUn/image.png" alt="Brian Gruttadauria" className="w-16 h-16 shrink-0 rounded-full object-cover ring-2 ring-[#8b5cf6]/60 shadow-lg" data-id="element-266" data-mp="op68c9qrcij8" />
              <div className="flex-1 text-center sm:text-left" data-id="element-267" data-mp="1cbw4wvkc7kwg">
                <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3 mb-1" data-id="element-268" data-mp="17e31ubebz70e">
                  <h3 className="font-heading text-[length:var(--h3-size)] text-white" data-id="element-269" data-mp="xqylvwniyxy">
                    Brian Gruttadauria
                  </h3>
                  <p className="text-[#8b5cf6] font-medium text-[length:var(--small-size)] uppercase tracking-wider" data-id="element-270" data-mp="3s2ifhqcvx4i8">
                    Board Member & Technical Advisor
                  </p>
                </div>
                <p className="text-[#7a8ba8] text-[length:var(--small-size)] mb-2" data-id="element-271" data-mp="2cldq7ywsd7rn">
                  Advises FIQIS on platform strategy, technical architecture,
                  and enterprise go-to-market execution.
                </p>
                <a href="https://www.linkedin.com/in/bgruttadauria/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[length:var(--small-size)] text-[#7a8ba8] hover:text-[#8b5cf6] transition-colors" data-id="element-272" data-mp="2raa78lgtlv4e">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" data-id="element-273" data-mp="358qb84nat7iu">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-274" data-mp="1aviiciivv5zu" />
                  </svg>
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Slide 19: Long-Term Vision (data-index=18) */}
      <section className="slide" data-index="18" data-id="element-275" data-mp="1hqim4cnb91bb">
        <div className="slide-content text-center w-full" data-id="element-276" data-mp="1vios55wr1rx1">
          <h2 className="font-heading text-[length:var(--h2-size)] mb-12 animate-on-view" data-id="element-277" data-mp="1qkn74ffrjiix">
            Long-Term Impact
          </h2>

          <div className="max-w-4xl mx-auto animate-on-view delay-100 mb-12" data-id="element-278" data-mp="25iz64sqlz3d9">
            <p className="font-heading text-[length:var(--h2-size)] text-gradient leading-tight" data-id="element-279" data-mp="20jcrcev4tf2c">
              "FIQIS aims to become a core infrastructure platform for financial
              institutions worldwide."
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mb-12 animate-on-view delay-200" data-id="element-280" data-mp="2o5f0pahf6itz">
            {['Secure integration infrastructure', 'AI-driven intelligence', 'Modern lending capabilities'].map((text, i) => <div key={i} className="bg-[#111d35] border border-[#00c8ff]/20 px-6 py-3 rounded-full text-[#f0f4ff] text-[length:var(--body-size)] shadow-lg" data-id="element-281" data-mp="20fu6fvftfz5e" data-mpi={i}>
                {text}
              </div>)}
          </div>

          <p className="text-[length:var(--h3-size)] text-[#7a8ba8] animate-on-view delay-300 max-w-3xl mx-auto" data-id="element-282" data-mp="1we8z0pm1wd2l">
            Enabling financial institutions to compete effectively in the
            evolving digital financial ecosystem.
          </p>
        </div>
      </section>

      {/* Slide 20: CTA (data-index=19) */}
      <section className="slide" data-index="19" data-id="element-283" data-mp="2pr8m21a08i5q">
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" data-id="element-284" data-mp="2ksxczsrjabok">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vw] max-w-2xl max-h-2xl bg-[#00c8ff] rounded-full mix-blend-screen filter blur-[120px] opacity-10 animate-pulse-glow" data-id="element-285" data-mp="2g1ytkkb90xh3"></div>
        </div>

        <div className="slide-content text-center" data-id="element-286" data-mp="26413rsdqqsmd">
          <h2 className="font-heading text-[length:var(--title-size)] mb-6 animate-on-view leading-tight max-w-4xl mx-auto" data-id="element-287" data-mp="261g2b4hcuep5">
            Let's Build the Future of Banking Together
          </h2>
          <p className="text-[length:var(--h3-size)] text-[#7a8ba8] mb-12 animate-on-view delay-100" data-id="element-288" data-mp="1x574fu90zo54">
            Ready to modernize your institution's infrastructure?
          </p>

          <a href="mailto:arthur@fiqis.com" className="inline-block bg-gradient-accent text-white font-heading font-bold text-[length:var(--body-size)] px-8 py-4 rounded-full shadow-[0_0_20px_rgba(0,200,255,0.4)] hover:shadow-[0_0_30px_rgba(0,200,255,0.6)] hover:scale-105 transition-all duration-300 animate-on-view delay-200" data-id="element-289" data-mp="3rgcd5cyyyqrb">
            arthur@fiqis.com
          </a>

          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-on-view delay-300 flex flex-col items-center gap-3" data-id="element-290" data-mp="7o222ylni7is">
            <img src="https://cdn.magicpatterns.com/uploads/oUNBTi9b5rsXiX3m9xY58X/blob-b68f2d9.webp" alt="FIQIS Logo" className="w-12 h-12 object-contain opacity-30" style={{
            filter: 'hue-rotate(80deg) saturate(1.5) brightness(1.4)',
            mixBlendMode: 'lighten'
          }} data-id="element-291" data-mp="3b2xrgbczvem7" />
            <span className="font-heading text-2xl font-bold tracking-widest text-white/20" data-id="element-292" data-mp="3pxluzjyu42z6">
              FIQIS
            </span>
          </div>
        </div>
      </section>
    </div>;
}